import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  BookOpen,
  Clock,
  Calendar,
  Users,
  MapPin,
  CheckCircle,
  Star,
  Award,
  FileText,
  Video,
  ArrowLeft,
  GraduationCap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CurriculumTab = ({ courseId, subjects }) => {
  const [syllabusList, setSyllabusList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/syllabus?course_id=${courseId}`)
      .then(res => setSyllabusList(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [courseId]);

  if (loading) {
    return <div className="py-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div></div>;
  }

  if (subjects.length === 0) {
    return (
      <div className="py-12 text-center">
        <BookOpen className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
        <p className="text-muted-foreground">Curriculum details will be updated soon.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="font-heading text-xl font-semibold">Course Curriculum</h3>
      {subjects.map((subject) => {
        const syllabus = syllabusList.find(s => s.subject === subject);
        const chapters = syllabus?.chapters || [];
        return (
          <div key={subject} className="border rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 bg-muted/40">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-primary" />
                </div>
                <h4 className="font-heading font-semibold text-base">{subject}</h4>
              </div>
              {syllabus?.file_url && (
                <Button size="sm" variant="outline" asChild>
                  <a href={syllabus.file_url} target="_blank" rel="noopener noreferrer">
                    <FileText className="w-3.5 h-3.5 mr-1.5" />
                    Download Syllabus
                  </a>
                </Button>
              )}
            </div>
            {chapters.length > 0 && (
              <div className="divide-y">
                {chapters.map((chapter, ci) => (
                  <div key={ci} className="px-5 py-3">
                    <p className="font-medium text-sm mb-1.5">
                      Chapter {ci + 1}: {chapter.name}
                    </p>
                    {chapter.topics?.length > 0 && (
                      <ul className="space-y-0.5 pl-2">
                        {chapter.topics.filter(t => t.trim()).map((topic, ti) => (
                          <li key={ti} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span className="w-1 h-1 rounded-full bg-muted-foreground/50 shrink-0" />
                            {topic}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const CourseDetailPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, token, user } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: '', email: '', phone: '', address: '' });

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/courses/${courseId}`);
        setCourse(response.data);
      } catch (error) {
        console.error('Error fetching course:', error);
        toast.error('Course not found');
        navigate('/courses');
      } finally {
        setLoading(false);
      }
    };
    fetchCourse();
  }, [courseId, navigate]);

  const handleEnrollClick = () => {
    if (isAuthenticated && user) {
      setLeadForm({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: '',
      });
    } else {
      setLeadForm({ name: '', email: '', phone: '', address: '' });
    }
    setEnrollDialogOpen(true);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadForm.name || !leadForm.email || !leadForm.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    setEnrolling(true);
    try {
      await axios.post(`${API_URL}/api/enrollments/lead`, {
        course_id: courseId,
        name: leadForm.name,
        email: leadForm.email,
        phone: leadForm.phone,
        address: leadForm.address || null,
      });
      setEnrollDialogOpen(false);
      toast.success('Your enrollment request has been submitted! Our team will contact you shortly.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit enrollment. Please try again.');
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (!course) return null;

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary via-primary to-accent py-16 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <Button variant="ghost" className="text-white/80 hover:text-white mb-6" onClick={() => navigate('/courses')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Courses
          </Button>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge className="bg-white/20">{course.category}</Badge>
                <Badge className="bg-white/20">{course.course_type}</Badge>
                {course.scholarship_available && (
                  <Badge className="bg-secondary text-secondary-foreground">Scholarship Available</Badge>
                )}
              </div>

              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                {course.name}
              </h1>

              <p className="text-lg text-white/80 mb-6 max-w-2xl">
                {course.description}
              </p>

              <div className="flex flex-wrap gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  <span>{course.duration}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span>Starts: {course.start_date}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  <span>{course.seats_available} seats left</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  <span>{course.mode_of_classes || course.course_type}</span>
                </div>
              </div>
            </div>

            {/* Enrollment Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="shadow-2xl" data-testid="enrollment-card">
                <CardContent className="p-6">
                  <div className="mb-6">
                    {course.discounted_fee && course.discounted_fee < course.fee ? (
                      <div>
                        <span className="font-heading text-4xl font-bold text-primary">
                          ₹{course.discounted_fee.toLocaleString()}
                        </span>
                        <span className="text-lg text-muted-foreground line-through ml-2">
                          ₹{course.fee.toLocaleString()}
                        </span>
                        <Badge className="ml-2 bg-green-100 text-green-700">
                          {Math.round(((course.fee - course.discounted_fee) / course.fee) * 100)}% OFF
                        </Badge>
                      </div>
                    ) : (
                      <span className="font-heading text-4xl font-bold text-primary">
                        ₹{course.fee?.toLocaleString()}
                      </span>
                    )}
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>Expert Faculty Support</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>Complete Study Materials</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>{course.test_series_included ? 'Test Series Included' : 'Regular Tests'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>Doubt Resolution</span>
                    </div>
                  </div>

                  <Button
                    className="w-full h-12 text-lg"
                    onClick={handleEnrollClick}
                    disabled={course.seats_available <= 0}
                    data-testid="enroll-btn"
                  >
                    {course.seats_available <= 0 ? 'Seats Full' : 'Enroll Now'}
                  </Button>

                  {course.scholarship_available && (
                    <Button variant="outline" className="w-full mt-3" asChild data-testid="apply-scholarship-btn">
                      <Link to={`/scholarship?course=${courseId}`}>Apply for Scholarship</Link>
                    </Button>
                  )}

                  <p className="text-xs text-center text-muted-foreground mt-4">
                    100% satisfaction guarantee. Contact us for any queries.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Course Details */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="w-full justify-start mb-6 bg-transparent border-b rounded-none h-auto p-0">
                  <TabsTrigger value="overview" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-6 py-3">
                    Overview
                  </TabsTrigger>
                  <TabsTrigger value="curriculum" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-6 py-3">
                    Curriculum
                  </TabsTrigger>
                  <TabsTrigger value="faculty" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-6 py-3">
                    Faculty
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="overview">
                  <div className="space-y-8">
                    <div>
                      <h3 className="font-heading text-xl font-semibold mb-4">About This Course</h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {course.description}
                      </p>
                    </div>

                    <div>
                      <h3 className="font-heading text-xl font-semibold mb-4">What You'll Learn</h3>
                      <div className="grid md:grid-cols-2 gap-3">
                        {(course.subjects || ['Physics', 'Chemistry', 'Mathematics', 'Biology']).map((subject, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                            <span>{subject}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="font-heading text-xl font-semibold mb-4">Course Includes</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <Card>
                          <CardContent className="flex items-center gap-4 p-4">
                            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Video className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">Live Classes</p>
                              <p className="text-sm text-muted-foreground">{course.batch_timing || 'Daily sessions'}</p>
                            </div>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="flex items-center gap-4 p-4">
                            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                              <FileText className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">Study Materials</p>
                              <p className="text-sm text-muted-foreground">Comprehensive notes & DPPs</p>
                            </div>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="flex items-center gap-4 p-4">
                            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                              <BookOpen className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">Test Series</p>
                              <p className="text-sm text-muted-foreground">Regular mock exams</p>
                            </div>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="flex items-center gap-4 p-4">
                            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Award className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">Certificate</p>
                              <p className="text-sm text-muted-foreground">On course completion</p>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="curriculum">
                  <CurriculumTab courseId={courseId} subjects={course.subjects || []} />
                </TabsContent>

                <TabsContent value="faculty">
                  <div className="space-y-4">
                    <h3 className="font-heading text-xl font-semibold mb-4">Meet Your Faculty</h3>
                    {course.faculty && course.faculty.length > 0 ? (
                      <div className="grid md:grid-cols-2 gap-4">
                        {course.faculty.map((faculty) => (
                          <Card key={faculty.id}>
                            <CardContent className="flex items-center gap-4 p-4">
                              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                                {faculty.photo_url ? (
                                  <img src={faculty.photo_url.startsWith('http') ? faculty.photo_url : `${API_URL}${faculty.photo_url.startsWith('/') ? '' : '/'}${faculty.photo_url}`} alt={faculty.name} className="w-full h-full rounded-full object-cover" />
                                ) : (
                                  <GraduationCap className="w-8 h-8 text-primary" />
                                )}
                              </div>
                              <div>
                                <p className="font-semibold">{faculty.name}</p>
                                <p className="text-sm text-muted-foreground">{faculty.subject_specialization?.join(', ')}</p>
                                <div className="flex items-center gap-1 mt-1">
                                  <Star className="w-4 h-4 fill-secondary text-secondary" />
                                  <span className="text-sm">{faculty.rating?.toFixed(1) || '4.5'}</span>
                                  <span className="text-sm text-muted-foreground">• {faculty.experience_years}+ years exp</span>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <Card>
                        <CardContent className="p-6 text-center">
                          <GraduationCap className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                          <p className="text-muted-foreground">Faculty details will be updated soon.</p>
                          <Button variant="link" asChild className="mt-2">
                            <Link to="/faculty">View All Faculty</Link>
                          </Button>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Course Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration</span>
                    <span className="font-medium">{course.duration}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Start Date</span>
                    <span className="font-medium">{course.start_date}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">End Date</span>
                    <span className="font-medium">{course.end_date}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Mode</span>
                    <span className="font-medium">{course.course_type}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Batch Timing</span>
                    <span className="font-medium">{course.batch_timing || 'Flexible'}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Seats</span>
                    <span className="font-medium">{course.total_seats}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Available</span>
                    <span className="font-medium text-green-600">{course.seats_available} seats</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-secondary/10 border-secondary/20">
                <CardContent className="p-6">
                  <Award className="w-10 h-10 text-secondary mb-4" />
                  <h4 className="font-heading font-semibold mb-2">Scholarship Available</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Merit-based scholarships up to 100% for deserving students.
                  </p>
                  <Button variant="outline" className="w-full" asChild>
                    <Link to="/scholarship">Learn More</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
      {/* Enrollment Dialog */}
      <Dialog open={enrollDialogOpen} onOpenChange={setEnrollDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enroll in {course.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLeadSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="lead-name">Name *</Label>
              <Input
                id="lead-name"
                value={leadForm.name}
                onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                placeholder="Your full name"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lead-email">Email *</Label>
              <Input
                id="lead-email"
                type="email"
                value={leadForm.email}
                onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                placeholder="your@email.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lead-phone">Phone *</Label>
              <Input
                id="lead-phone"
                value={leadForm.phone}
                onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                placeholder="Your phone number"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lead-address">Address</Label>
              <Input
                id="lead-address"
                value={leadForm.address}
                onChange={(e) => setLeadForm({ ...leadForm, address: e.target.value })}
                placeholder="Your address (optional)"
              />
            </div>
            <Button type="submit" className="w-full" disabled={enrolling}>
              {enrolling ? 'Submitting...' : 'Submit Enrollment'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default CourseDetailPage;
